import { ActionMenu } from '@fox/atoms/src/action-menu/index';
import { ConsumeContainer, useMeasureRef } from '@fox/atoms/src/container-context/index';
import { FullScreenContextProvider } from '@fox/atoms/src/full-screen/index';
import { NoData } from '@fox/atoms/src/no-data/index';
import { Skeleton } from '@mui/material';
import { useSubscribeTopicWithHandler } from '@fox/extensions/src/extension-hooks';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FoxColumnType } from '../contracts/table';
import { HoverRowVisible } from '../data-grid-table/components/common';
import { DataGrid } from '../data-grid-table/data-grid';
import { Toolbar } from '../grid-toolbar/index';
import { DefaultPageSizeOptions } from '../pagination/index';
import { AutoColumnTitle } from './column-title';
import { AutoDataGridContextProvider, PivotContextProvider, useAutoDataGridContext } from './context';
import { AutoGridProps } from './types';

// re-export filters for consumption
export * from './context';
export * from './create-popover-button';
export * from './filters/date-range-filters';
export * from './filters/number-range-filter';
export * from './filters/text-filter';
export * from './settings-menu';
export * from './types';

const AUTO_CONTEXT_MENU = 'autoContextMenu';

function Menu<T extends Record<string, any>>({
	buildRowActionMenu: useRowActionMenu,
	row,
	buildActionIcons
}: Pick<AutoGridProps<T>, 'buildRowActionMenu' | 'buildActionIcons'> & { row: T }) {
	// HACK - we have used useRowActionMenu as a hook in various grids, however ideally we actually use the non-hook / async functions ....
	// I am expecting by calling arguably invalid code, this will hint to the react compiler to not optimise this component
	const options = useRowActionMenu?.(row);
	return (
		<HoverRowVisible style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			{buildActionIcons && buildActionIcons.icons(row)}
			{options ? <ActionMenu options={options} mainIconSize="20px" /> : null}
		</HoverRowVisible>
	);
}

export function InnerAutoDataGrid<T = Record<string, any>>({
	loading,
	columns,
	data,
	total,
	height,
	rowHeight,
	hideColumnBorders,
	getRowKey,
	noDataMessage,
	rowClassName,
	virtualPage,
	buildRowActionMenu,
	buildColumnActionMenu,
	buildToolbarActionMenu,
	buildActionIcons,
	hideToolbar,
	rowActionTitle,
	scrollToIndex,
	hiddenColumnsFetch,
	toolbar,
	rowSelectionExtension,
	paddedToolbar
}: AutoGridProps<T>) {
	const { pinnedColumnsDataIndex, sort, setOrder, setWidths, order, widths, hiddenColumnsDataIndex, groupBy } = useAutoDataGridContext();
	const tableRef = useRef(null);
	const cols: FoxColumnType<T>[] = useMemo(() => {
		const indexes = columns.map(a => a.dataIndex).filter(a => !!a && !hiddenColumnsDataIndex.includes(a));

		// whenever the columns change, we can register our new function that specifies this
		hiddenColumnsFetch?.(() => hiddenColumnsDataIndex);

		// handle the case where sort state is out of sync with the actual columns of the grid
		const allCols = [...order.filter(a => indexes.includes(a)), ...indexes.filter(a => !order.includes(a))];
		// add the action column if required
		(buildRowActionMenu || buildActionIcons) && allCols.push(AUTO_CONTEXT_MENU);
		return allCols.map(o => {
			const c = columns.find(a => a.dataIndex === o);

			if (!c && o === AUTO_CONTEXT_MENU) {
				return {
					dataIndex: 'actions',
					width: (buildRowActionMenu ? 45 : 0) + (buildActionIcons ? buildActionIcons.width : 0),
					fixed: 'right',
					padCell: false,
					title: rowActionTitle ? rowActionTitle : <></>,
					render: (_, row, __) => <Menu row={row} buildRowActionMenu={buildRowActionMenu} buildActionIcons={buildActionIcons} />
				} as FoxColumnType<T>;
			}
			const columnPinned = pinnedColumnsDataIndex.includes(c.dataIndex);
			const fixTo =
				c.fixed == 'left' || (columnPinned && c.fixed == 'toggle-left')
					? 'left'
					: c.fixed == 'right' || (columnPinned && c.fixed == 'toggle-right')
						? 'right'
						: c.fixed === undefined
							? columnPinned
								? 'left'
								: undefined
							: undefined;
			return {
				columnKey: c.columnKey,
				dataIndex: c.dataIndex,
				width: widths[c.dataIndex] ?? c.initialWidth,
				padCell: typeof c.padCell === 'boolean' ? c.padCell : true,
				fixed: fixTo,
				columnFooter: c.columnFooter,
				alwaysRerender: c?.alwaysRerender ?? false,
				render: c.hoverVisible
					? (a, b, i) => {
							const rendered = c.render(a, b, i);
							return <HoverRowVisible>{rendered}</HoverRowVisible>;
						}
					: c.render,
				title: c.hideTitle ? (
					<></>
				) : (
					<AutoColumnTitle col={c} key={c.dataIndex} buildOptionsMenu={buildColumnActionMenu} hideToolbar={hideToolbar} hidePinAndHide={columns.length <= 1} />
				)
			} as FoxColumnType<T>;
		});
	}, [columns, sort, widths, order, pinnedColumnsDataIndex, hiddenColumnsDataIndex /*, buildActionIcons, buildRowActionMenu*/]);

	const updateColumnSizes = useCallback(
		function updateColumnSizes(val: FoxColumnType<T>[]) {
			const w = val.reduce((prev, cur) => ({ ...prev, [cur.dataIndex]: cur.width }), {} as Record<string, number>);
			setWidths(w);
		},
		[setWidths]
	);

	const updateColumnOrder = useCallback(
		function updateColumnOrder(val: FoxColumnType<T>[]) {
			setOrder(val.map(a => a.dataIndex));
		},
		[setOrder]
	);

	let pivotData: { pivotName: string; pivotValue: string; pivotData: T[]; colour?: string }[] = [];
	if (groupBy) {
		pivotData = columns.find(c => c.dataIndex === groupBy)?.groupByFunction(groupBy, data);
	}

	const [ref, measure] = useMeasureRef();

	// columns coming in are slightly different to what AutoDataGrid expects fortunately the conversion is quite easy
	return (
		<FullScreenContextProvider containerElementRef={tableRef}>
			<ConsumeContainer measureConsumption={measure}>
				<div ref={tableRef}>
					{!hideToolbar && (
						<Toolbar
							ref={ref}
							paddedToolbar={paddedToolbar}
							data={data}
							total={total}
							items={toolbar}
							columns={columns}
							buildToolbarActionMenu={buildToolbarActionMenu}
						/>
					)}

					{pivotData?.length < 1 ? (
						<DataGrid
							data={data}
							getRowKey={getRowKey}
							total={total}
							columns={cols}
							height={height}
							virtualPage={virtualPage}
							rowClassName={rowClassName}
							loadingData={loading}
							noDataPlaceHolder={noDataMessage ? <NoData message={noDataMessage} /> : undefined}
							onColumnOrderChanged={updateColumnOrder}
							onColumnResized={updateColumnSizes}
							scrollToIndex={scrollToIndex}
							rowSelectionExtension={rowSelectionExtension}
							rowHeight={rowHeight}
							hideColumnBorders={hideColumnBorders}
						/>
					) : (
						<div style={{ height: '100%', overflowY: 'auto' }}>
							{pivotData?.map(pivot => {
								return (
									<div style={{ marginBottom: 10 }} key={pivot.pivotName}>
										<div
											style={{
												backgroundColor: pivot?.colour,
												color: 'black',
												padding: 5,
												borderRadius: 5,
												marginBottom: 10
											}}
										>
											{pivot.pivotName}
										</div>
										<PivotContextProvider value={{ groupBy, groupByValue: pivot.pivotValue }}>
											<DataGrid
												maxHeight={Math.min(800, window.innerHeight - 200)}
												data={pivot.pivotData}
												getRowKey={getRowKey}
												total={pivot.pivotData.length}
												columns={cols}
												virtualPage={virtualPage}
												rowClassName={rowClassName}
												loadingData={loading}
												noDataPlaceHolder={noDataMessage ? <NoData message={noDataMessage} /> : undefined}
												onColumnOrderChanged={updateColumnOrder}
												onColumnResized={updateColumnSizes}
												scrollToIndex={scrollToIndex}
												rowSelectionExtension={rowSelectionExtension}
												rowHeight={rowHeight}
											/>
										</PivotContextProvider>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</ConsumeContainer>
		</FullScreenContextProvider>
	);
}

export function AutoDataGrid<T = Record<string, any>>({
	loading,
	columns,
	defaultSort,
	filters,
	setFilters,
	onSort,
	sort,
	filterParams,
	addColumnFilterParams,
	id,
	search,
	setSearch,
	pinnedColumnsDataIndex,
	setPinnedColumnsDataIndex,
	colOrder,
	setColOrder,
	colVis,
	setColVis,
	groupBy,
	setGroupBy,
	localSaveOnly,
	rowHeight,
	hideColumnBorders,
	...rest
}: AutoGridProps<T>) {
	return (
		<AutoDataGridContextProvider
			sort={sort ?? defaultSort}
			onSort={onSort}
			filters={filters}
			setFilters={setFilters}
			filterParams={filterParams}
			addColumnFilterParams={addColumnFilterParams}
			loading={loading}
			columns={columns}
			id={id}
			search={search}
			setSearch={setSearch}
			pinnedColumnsDataIndex={pinnedColumnsDataIndex}
			setPinnedColumnsDataIndex={setPinnedColumnsDataIndex}
			colOrder={colOrder}
			setColOrder={setColOrder}
			colVis={colVis}
			setColVis={setColVis}
			groupBy={groupBy}
			setGroupBy={setGroupBy}
			localSaveOnly={localSaveOnly}
		>
			<InnerAutoDataGrid {...rest} id={id} loading={loading} columns={columns} rowHeight={rowHeight} hideColumnBorders={hideColumnBorders} />
		</AutoDataGridContextProvider>
	);
}

/** This builds and tracks pagination object with an optional callback to pass up the page changes
 * @param updatePage An optional callback to notify the consumer of pagination that the value has changed.
 */
export function usePagination(updatePage?: (page: number) => void) {
	const [currentPage, setCurrentPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(20);
	return useMemo(
		() => ({
			current: currentPage + 1,
			pageSize,
			pageSizeOptions: DefaultPageSizeOptions,
			onChange: (page, pageSize) => {
				setPageSize(pageSize);
				setCurrentPage(page - 1);
				updatePage?.(page);
			}
		}),
		[currentPage, pageSize, updatePage]
	);
}

export type LazyCellProps<TKey, TResolvedValue> = {
	value: TKey;
	onResolveValue: (value: TKey) => Promise<TResolvedValue>;
	renderResolvedValue: (rv: TResolvedValue) => React.ReactNode;
	renderUnresolvedValue: (e: Error) => React.ReactNode;
	refetchTopic?: string;
	clear?: (val: TKey) => void;
};

export function LazyCell<T, U>({ value, onResolveValue, renderResolvedValue, renderUnresolvedValue, refetchTopic, clear }: LazyCellProps<T, U>) {
	const mounted = React.useRef(false);
	const [resolvedValue, setResolvedValue] = React.useState(undefined);
	const [unresolvedError, setUnresolvedError] = React.useState<Error | undefined>(undefined);
	const [renderKey, setRenderKey] = useState(nanoid());
	useSubscribeTopicWithHandler(refetchTopic, () => {
		clear?.(value);
		setRenderKey(nanoid());
	});

	React.useEffect(() => {
		mounted.current = true;
		onResolveValue(value)
			.then(r => {
				if (mounted.current) setResolvedValue(r);
			})
			.catch(e => {
				if (mounted.current) setUnresolvedError(e);
			});
		return () => {
			mounted.current = false;
		};
	}, [renderKey]);

	if (unresolvedError) {
		return <>{renderUnresolvedValue(unresolvedError)}</>;
	} else if (resolvedValue !== undefined) {
		return <>{renderResolvedValue(resolvedValue)}</>;
	} else {
		return <Skeleton />;
	}
}
